/* Form de criar/editar um TÓPICO de FAQ/capacidades (ADR-0658 + ADR-0720). Um tópico = slug +
   metadados compartilhados (categoryKey/ordem) + uma variação por idioma (pt-BR/en-US) editada em
   abas. A CHAVE de categoria (`categoryKey`, slug estável) é compartilhada e fica no topo; o RÓTULO
   (`categoryLabel`, traduzível) é editado DENTRO de cada aba de idioma. `topic` ausente = criar
   (slug editável); presente = editar (slug é a identidade estável, read-only). Salvar faz upsert por
   (slug,locale) para cada idioma preenchido (pergunta+resposta+rótulo): idiomas vazios são ignorados.
   Regra de ouro: DialogContent locked enquanto salva. */
import {
  createFaqBody,
  FAQ_LOCALES,
  type FaqLocale,
  type FaqStatus,
  type FaqTopic,
} from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from '@planuze/ui';
import { useState } from 'react';
import { useFaqAdminContent } from '../../content/sections/faq-admin.js';
import { type SaveFaqTopicLocale, useSaveFaqTopic } from '../../hooks/use-faq.js';
import { faqErrorMessage } from './faq-error.js';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly topic?: FaqTopic | undefined;
  /* Categoria pré-fixada e read-only (ADR-0728): surfaces dedicadas (ex.: o "Como
     Funciona" do publisher) criam tópicos sob uma `categoryKey` fixa (publisher-guide)
     sem o staff precisar conhecê-la. Ausente = campo livre (tela geral de FAQ). */
  readonly fixedCategoryKey?: string | undefined;
}

/** Rascunho editável de uma variação de idioma. `existingId` presente = já persistida (update).
    `categoryLabel` = rótulo da categoria naquele idioma. */
interface LocaleDraft {
  readonly locale: FaqLocale;
  readonly categoryLabel: string;
  readonly question: string;
  readonly answer: string;
  readonly status: FaqStatus;
  readonly existingId?: string;
}

function buildDrafts(topic: FaqTopic | undefined): LocaleDraft[] {
  return FAQ_LOCALES.map((locale) => {
    const existing = topic?.locales.find((l) => l.locale === locale);
    if (existing === undefined) {
      return { locale, categoryLabel: '', question: '', answer: '', status: 'draft' };
    }
    return {
      locale,
      categoryLabel: existing.categoryLabel,
      question: existing.question,
      answer: existing.answer,
      status: existing.status,
      existingId: existing.id,
    };
  });
}

function isFilled(draft: LocaleDraft): boolean {
  return draft.question.trim() !== '' && draft.answer.trim() !== '';
}

export function FaqFormModal({ open, onClose, topic, fixedCategoryKey }: Props) {
  const isEdit = topic !== undefined;
  const [slug, setSlug] = useState(topic?.slug ?? '');
  const [categoryKey, setCategoryKey] = useState(
    topic?.categoryKey ?? fixedCategoryKey ?? 'capacidades',
  );
  const [sortOrder, setSortOrder] = useState(String(topic?.sortOrder ?? 0));
  const [drafts, setDrafts] = useState<LocaleDraft[]>(() => buildDrafts(topic));
  const [error, setError] = useState<string | null>(null);

  const save = useSaveFaqTopic();
  const saving = save.isPending;
  const content = useFaqAdminContent();
  const copy = content.formModal;
  const localeLabels = content.localeLabels;

  const patchDraft = (locale: FaqLocale, patch: Partial<Omit<LocaleDraft, 'locale'>>): void => {
    setDrafts((prev) => prev.map((d) => (d.locale === locale ? { ...d, ...patch } : d)));
  };

  const onSubmit = (): void => {
    setError(null);
    const orderNum = Number.parseInt(sortOrder, 10);
    const filled = drafts.filter(isFilled);
    if (filled.length === 0) {
      setError(copy.missingFieldsError);
      return;
    }
    /* Valida slug + categoryKey + campos de cada idioma preenchido reusando o contrato (slug é o
       mesmo em edição, apenas read-only). `createFaqBody` cobre slug/categoryKey/categoryLabel/
       pergunta/resposta/status/ordem. */
    for (const draft of filled) {
      const parsed = createFaqBody.safeParse({
        slug,
        categoryKey,
        categoryLabel: draft.categoryLabel,
        question: draft.question,
        answer: draft.answer,
        locale: draft.locale,
        status: draft.status,
        sortOrder: orderNum,
      });
      if (!parsed.success) {
        setError(copy.invalidFieldsError(localeLabels[draft.locale]));
        return;
      }
    }
    const locales: SaveFaqTopicLocale[] = filled.map((draft) => ({
      locale: draft.locale,
      categoryLabel: draft.categoryLabel,
      question: draft.question,
      answer: draft.answer,
      status: draft.status,
      ...(draft.existingId !== undefined ? { existingId: draft.existingId } : {}),
    }));
    save.mutate(
      { slug, categoryKey, sortOrder: orderNum, locales },
      {
        onSuccess: () => {
          toast.success(isEdit ? copy.updatedToast : copy.createdToast);
          onClose();
        },
        onError: (err) => toast.error(faqErrorMessage(err, content.errors)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent locked={saving} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? copy.editTitle : copy.createTitle}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {error !== null && <InlineAlert variant="destructive">{error}</InlineAlert>}
          <div className="grid grid-cols-[2fr_2fr_1fr] gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="faq-slug">{copy.slugLabel}</Label>
              <Input
                id="faq-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                disabled={isEdit}
                placeholder={copy.slugPlaceholder}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="faq-category-key">{copy.categoryKeyLabel}</Label>
              <Input
                id="faq-category-key"
                value={categoryKey}
                onChange={(e) => setCategoryKey(e.target.value)}
                placeholder={copy.categoryKeyPlaceholder}
                disabled={fixedCategoryKey !== undefined}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="faq-order">{copy.orderLabel}</Label>
              <Input
                id="faq-order"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
          </div>
          <InlineAlert variant="info">{copy.sharedInfo}</InlineAlert>
          <Tabs defaultValue={FAQ_LOCALES[0]}>
            <TabsList>
              {drafts.map((draft) => (
                <TabsTrigger key={draft.locale} value={draft.locale}>
                  {localeLabels[draft.locale]}
                  {isFilled(draft) && (
                    <span
                      className="ml-1.5 size-1.5 rounded-full bg-success"
                      aria-hidden="true"
                      title={copy.filledTitle}
                    />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
            {drafts.map((draft) => (
              <TabsContent key={draft.locale} value={draft.locale} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`faq-category-label-${draft.locale}`}>
                    {copy.categoryLabelLabel}
                  </Label>
                  <Input
                    id={`faq-category-label-${draft.locale}`}
                    value={draft.categoryLabel}
                    onChange={(e) => patchDraft(draft.locale, { categoryLabel: e.target.value })}
                    placeholder={
                      draft.locale === 'pt-BR'
                        ? copy.categoryLabelPlaceholderPt
                        : copy.categoryLabelPlaceholderEn
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`faq-question-${draft.locale}`}>{copy.questionLabel}</Label>
                  <Input
                    id={`faq-question-${draft.locale}`}
                    value={draft.question}
                    onChange={(e) => patchDraft(draft.locale, { question: e.target.value })}
                    placeholder={copy.questionPlaceholder}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`faq-answer-${draft.locale}`}>{copy.answerLabel}</Label>
                  <Textarea
                    id={`faq-answer-${draft.locale}`}
                    value={draft.answer}
                    onChange={(e) => patchDraft(draft.locale, { answer: e.target.value })}
                    rows={8}
                    placeholder={copy.answerPlaceholder}
                  />
                </div>
                <div className="flex w-40 flex-col gap-1.5">
                  <Label>{copy.statusLabel}</Label>
                  <Select
                    value={draft.status}
                    onValueChange={(v) => patchDraft(draft.locale, { status: v as FaqStatus })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">{copy.statusDraft}</SelectItem>
                      <SelectItem value="published">{copy.statusPublished}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {copy.cancel}
          </Button>
          <Button onClick={onSubmit} loading={saving}>
            {isEdit ? copy.save : copy.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
